import { message } from "antd";
import { classRoomRegistration } from "lib/common/endpoints/endUser";
import { getClassroomSections } from "lib/common/endpoints/public";
import { Classroom } from "lib/common/types/classroom";
import { Registrations } from "lib/common/types/registration";
import usePost from "lib/hooks/usePost";
import React, { useEffect, useState } from "react";
import useSWR from "swr";
import { useAppSelector } from "redux/hooks";
import { getUser } from "redux/reducers/userSlice";
import Button from "../atomic/Button";
import { ButtonSpinner } from "../atomic/Spinner/Spinner";
import Select from "../atomic/Select/Select";
import TextArea from "../atomic/TextArea";
import TextField from "../atomic/TextField";
import { Form } from "../Form";
import FormControl from "../Form/FormControl";
import Spacer from "../Form/Spacer";
import { getOrCreateDeviceId } from "utils/helper/deviceId";

interface RegisterFormProps extends React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> {
  onNext?: () => void;
  classRoom: Classroom;
  registration: Registrations;
  refetch: () => void;
}

function RegisterForm({
  children,
  onNext,
  classRoom,
  registration,
  refetch,
}: RegisterFormProps) {
  const user = useAppSelector(getUser);
  const [selectedShift, setSelectedShift] = useState<string>("");
  // Admin-managed (xylene/admin_panel/class_section) and scoped to this
  // classroom (classroom.sections, set on its edit form) instead of a
  // hardcoded list shown everywhere -- a class with morning/evening
  // batches offers both here; a single-batch class offers none/one.
  const { data: sectionsData } = useSWR(
    classRoom?._id?.$oid ? getClassroomSections + classRoom._id.$oid : null,
  );
  const sections: string[] = sectionsData?.data?.sections ?? [];
  // A single available section isn't a real choice -- the dropdown below
  // only renders when there's more than one -- but nothing was ever
  // setting selectedShift in that case, so submit silently sent
  // shift: "" and the backend rejects registration outright (shift is a
  // required, non-empty field). Auto-select the only option instead.
  useEffect(() => {
    if (sections.length === 1 && !selectedShift) {
      setSelectedShift(sections[0]);
    }
  }, [sections, selectedShift]);
  const {
    data,
    error,
    isLoading: loading,
    post: register,
  } = usePost<{ operation: string; success: boolean; message: string }>(
    classRoomRegistration + classRoom?._id.$oid,
  );

  useEffect(() => {
    if (data?.success) {
      message?.success(data.message);
      refetch();
    }
  }, [data, refetch]);

  useEffect(() => {
    if (error?.message) {
      message.error(error.message);
      refetch();
    }
  }, [error, refetch]);

  return (
    <div className="w-full min-w-0">
      <Form
        onSubmit={(val) => {
          if (sections.length > 1 && !selectedShift) {
            message.error("Please select your preferred shift before submitting.");
            return;
          }
          register({ ...val, shift: selectedShift, deviceId: getOrCreateDeviceId() });
        }}
        className="max-w-sm"
      >
        <Spacer className="my-2">
          <FormControl
            label="First Name"
            name="firstName"
            required
            defaultValue={user?.fullName?.split(" ")[0] ?? ""}
          >
            <TextField className="bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600" />
          </FormControl>
        </Spacer>
        <Spacer className="my-2">
          <FormControl
            label="Last Name"
            name="lastName"
            required
            defaultValue={user?.fullName?.split(" ")[1] ?? ""}
          >
            <TextField className="bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600" />
          </FormControl>
        </Spacer>
        <Spacer className="my-2">
          <FormControl
            label="College / Institution"
            name="institution"
            required
            defaultValue={user?.institution ?? ""}
          >
            <TextField className="bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600" />
          </FormControl>
        </Spacer>
        <Spacer className="my-2">
          <FormControl
            label="Address"
            name="address"
            required
            defaultValue={user?.address ?? ""}
          >
            <TextField className="bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600" />
          </FormControl>
        </Spacer>
        <Spacer className="my-2">
          <FormControl
            label="Phone Number"
            name="contact"
            defaultValue={user?.userData?.mobile ?? ""}
          >
            {/* Hard cap at 10 digits: the account mobile prefilled above is
                sometimes "+977-98XXXXXXXX" (with a country-code prefix),
                and without a maxLength a user who types their own number
                on top of that prefill instead of clearing it first ends
                up submitting both concatenated as one unusable value. */}
            <TextField
              type="tel"
              inputMode="numeric"
              maxLength={10}
              className="bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600"
              placeholder="98XXXXXXXX"
            />
          </FormControl>
        </Spacer>
        {sections.length > 1 && (
          <Spacer className="my-2">
            <FormControl label="Preferred Shift" name="shift" required>
              <Select
                className="bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 hover:ring-1"
                name="shift"
                id="shift"
                options={sections}
                value=""
                optionLabel="Select preferred shift"
                onChange={(e) =>
                  setSelectedShift(
                    (e.target as HTMLElement).id ||
                      (e.target as HTMLElement).innerText ||
                      "",
                  )
                }
              />
            </FormControl>
          </Spacer>
        )}
        <Spacer className="my-2">
          <FormControl
            label="How did you hear about us?"
            name="extraMessage"
          >
            <TextArea className="bg-gray-50 dark:bg-gray-700 dark:text-gray-100 rounded-xl border border-gray-200 dark:border-gray-600" />
          </FormControl>
        </Spacer>
        <Button type="submit" className="mt-5" block disabled={loading}>
          {loading ? (
            <div>
              <span className="mr-3">Submitting...</span>
              <ButtonSpinner />
            </div>
          ) : (
            <span>Submit</span>
          )}
        </Button>
      </Form>
    </div>
  );
}

export default RegisterForm;
